import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatApiError } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Plus, Search, Edit, Trash2, Users, Download, UserCircle, FileText } from 'lucide-react';
import { toast } from 'sonner';

const JOB_ROLES = ['Sales Girl', 'Sales Man', 'Manager', 'COO', 'CFO', 'CCO', 'G.M', 'R.M', 'Purchase Manager', 'Marketing Manager', 'Digital Marketing Manager', 'Financial Analyst', 'Accountant', 'Billing Staff', 'Godown Incharge', 'Driver', 'P.R.O', 'H.R', 'Assistant Manager', 'Floor Manager', 'House Keeping', 'Packing Staff', 'Part Time'];
const SYSTEM_ROLES = ['employee', 'branch_manager', 'management', 'super_admin'];
const DIVISIONS = ['Konattu Agencies', 'Konattu Hypermart', 'Kitchen Lounges', 'Emilia', 'Konattu Inhome'];
const BRAND_CLASSIFICATIONS = ['LG', 'IFB', 'Haier', 'Whirlpool', 'Godrej', 'Vivo', 'Oppo', 'Prestige', 'Havells', 'Lloyd', 'Impex', 'Konattu'];
// Employee lifecycle statuses (matches backend core.EMP_STATUSES minus legacy `exited`).
const EMP_STATUSES = ['active', 'resigned', 'absconding', 'suspended', 'terminated'];
const STATUS_BADGE = {
  active: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  resigned: 'bg-amber-50 text-amber-800 border-amber-200',
  absconding: 'bg-red-50 text-red-700 border-red-200',
  suspended: 'bg-orange-50 text-orange-800 border-orange-200',
  terminated: 'bg-neutral-100 text-neutral-700 border-neutral-300',
  exited: 'bg-neutral-100 text-neutral-500 border-neutral-300',
};

const EMPTY_FORM = {
  name: '', email: '', phone: '', branch_id: '', system_role: 'employee', job_role: '', password: '',
  entry_time: '10:00', exit_time: '18:00',
  sunday_entry_time: '', sunday_exit_time: '',
  dob: '', gender: '', address: '', joining_date: '', emergency_contact: '', emergency_name: '',
  blood_group: '', aadhar_number: '', pan_number: '', bank_account: '', bank_name: '', ifsc_code: '',
  education: '', experience_years: '', previous_company: '',
  uan_number: '', esi_number: '', division: '', brand_classification: '',
  emp_status: 'active', exit_date: '', exit_reason: '',
  require_achievement_before_attendance: false
};

export default function EmployeesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filterBranch, setFilterBranch] = useState('all');
  const [filterRole, setFilterRole] = useState('all');
  const [filterDivision, setFilterDivision] = useState('all');
  const [filterStatus, setFilterStatus] = useState('active');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingIsPrimary, setEditingIsPrimary] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  // Super Admin-only credential reset (kept isolated from the main employee form)
  const [creds, setCreds] = useState({ email: '', password: '' });
  const [brandList, setBrandList] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setEmployees([]);
    try {
      const params = {};
      if (filterBranch && filterBranch !== 'all') params.branch_id = filterBranch;
      if (filterRole && filterRole !== 'all') params.job_role = filterRole;
      if (filterDivision && filterDivision !== 'all') params.division = filterDivision;
      if (filterStatus && filterStatus !== 'all') params.emp_status = filterStatus;
      if (search) params.search = search;
      const [empRes, brRes, bLRes] = await Promise.all([
        api.get('/employees', { params }),
        api.get('/branches'),
        api.get('/brand-list').catch(() => ({ data: [] }))
      ]);
      setEmployees(empRes.data);
      setBranches(brRes.data);
      setBrandList(bLRes.data || []);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [filterBranch, filterRole, filterDivision, filterStatus, search]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const getBranchName = (bid) => branches.find(b => b.id === bid)?.name || '-';

  const handleSave = async () => {
    // Strict validations (phone 10, ESI 10, UAN 12 digits)
    const digits = (s) => String(s || '').replace(/\D/g, '');
    if (form.phone && digits(form.phone).length !== 10) { toast.error('Mobile number must be exactly 10 digits'); return; }
    if (form.esi_number && digits(form.esi_number).length !== 10) { toast.error('ESI number must be exactly 10 digits'); return; }
    if (form.uan_number && digits(form.uan_number).length !== 12) { toast.error('UAN number must be exactly 12 digits'); return; }
    try {
      if (editingId) {
        // Full payload update so re-assignment & division changes persist
        const payload = { ...form };
        delete payload.email;
        delete payload.password;
        if (payload.experience_years === '') payload.experience_years = null;
        await api.put(`/employees/${editingId}`, payload);
        // Super Admin: also persist credential changes (email/password) via dedicated route
        if (user?.system_role === 'super_admin') {
          const editingEmp = employees.find(e => e.id === editingId);
          const credsPayload = {};
          const emailChanged = creds.email && creds.email.trim().toLowerCase() !== (editingEmp?.email || '').toLowerCase();
          if (emailChanged) credsPayload.email = creds.email.trim();
          if (creds.password && creds.password.trim()) credsPayload.password = creds.password.trim();
          if (Object.keys(credsPayload).length > 0) {
            await api.put(`/employees/${editingId}/credentials`, credsPayload);
          }
        }
        toast.success('Employee updated');
      } else {
        if (!form.password?.trim()) { toast.error('Login password is required'); return; }
        const payload = { ...form };
        if (payload.experience_years === '') payload.experience_years = null;
        await api.post('/employees', payload);
        toast.success('Employee created');
      }
      setDialogOpen(false);
      setEditingId(null);
      setEditingIsPrimary(false);
      setForm(EMPTY_FORM);
      setCreds({ email: '', password: '' });
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const handleEdit = (emp) => {
    setEditingId(emp.id);
    setEditingIsPrimary(!!emp.is_primary_admin);
    setCreds({ email: emp.email || '', password: '' });
    setForm({
      name: emp.name, email: emp.email, phone: emp.phone,
      branch_id: emp.branch_id || '', system_role: emp.system_role, job_role: emp.job_role, password: '',
      dob: emp.dob || '', gender: emp.gender || '', address: emp.address || '',
      joining_date: emp.joining_date || '', emergency_contact: emp.emergency_contact || '',
      emergency_name: emp.emergency_name || '', blood_group: emp.blood_group || '',
      aadhar_number: emp.aadhar_number || '', pan_number: emp.pan_number || '',
      bank_account: emp.bank_account || '', bank_name: emp.bank_name || '', ifsc_code: emp.ifsc_code || '',
      education: emp.education || '', experience_years: emp.experience_years || '',
      previous_company: emp.previous_company || '',
      uan_number: emp.uan_number || '', esi_number: emp.esi_number || '',
      division: emp.division || '', brand_classification: emp.brand_classification || '',
      entry_time: emp.entry_time || '10:00', exit_time: emp.exit_time || '18:00',
      sunday_entry_time: emp.sunday_entry_time || '', sunday_exit_time: emp.sunday_exit_time || '',
      emp_status: emp.emp_status || 'active',
      exit_date: emp.exit_date || '',
      exit_reason: emp.exit_reason || '',
      require_achievement_before_attendance: !!emp.require_achievement_before_attendance
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this employee?')) return;
    try {
      await api.delete(`/employees/${id}`);
      toast.success('Employee deleted');
      fetchData();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  // Download Experience Certificate PDF for relieved/exited employees.
  // Admin-only — the route is gated server-side; we additionally hide the
  // button for non-admins and for active employees in the markup above.
  const handleDownloadCertificate = async (emp) => {
    try {
      const res = await api.get(`/employees/${emp.id}/experience-certificate`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = `experience_certificate_${(emp.name || 'employee').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Experience certificate downloaded');
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const canManage = ['super_admin', 'management', 'branch_manager'].includes(user?.system_role);
  // Only Super Admin & Management can reassign structural fields (Branch, Division, Job Role, Brand, System Role).
  const canEditStructural = ['super_admin', 'management'].includes(user?.system_role);
  // Employee lifecycle status is HR-only: Super Admin, Management, or a user
  // whose job_role is 'H.R'. Backend enforces the same rule.
  const canChangeStatus = ['super_admin', 'management'].includes(user?.system_role) || user?.job_role === 'H.R';
  // In edit mode, branch_manager sees these as read-only. In create mode, everyone with canManage can set them.
  const lockStructural = !!editingId && !canEditStructural;

  const handleExportCSV = () => {
    const token = localStorage.getItem('hrms_token');
    const base = process.env.REACT_APP_BACKEND_URL;
    fetch(`${base}/api/export/employees`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob())
      .then(blob => {
        const a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'employees.csv';
        a.click();
      });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="employees-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-black text-neutral-950 text-2xl sm:text-3xl tracking-tight">Employees</h1>
          <p className="text-neutral-500 text-sm mt-1">{employees.length} staff members</p>
        </div>
        {canManage && (
          <div className="flex gap-2">
            <Button variant="outline" className="rounded-sm" onClick={handleExportCSV} data-testid="export-employees-button">
              <Download className="h-4 w-4 mr-2" /> Export CSV
            </Button>
            <Button className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="add-employee-button"
              onClick={() => { setEditingId(null); setEditingIsPrimary(false); setForm(EMPTY_FORM); setCreds({ email: '', password: '' }); setDialogOpen(true); }}>
              <Plus className="h-4 w-4 mr-2" /> Add Employee
            </Button>
          </div>
        )}
      </div>

      {/* Filters */}
      <Card className="border border-neutral-200 shadow-sm rounded-md">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-neutral-400" />
              <Input placeholder="Search employees..." value={search} onChange={e => setSearch(e.target.value)}
                className="pl-9 h-10 rounded-sm border-neutral-200" data-testid="employee-search-input" />
            </div>
            {user?.system_role !== 'branch_manager' && (
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger className="w-full sm:w-48 h-10 rounded-sm" data-testid="filter-branch-select">
                  <SelectValue placeholder="All Branches" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Branches</SelectItem>
                  {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-44 h-10 rounded-sm" data-testid="filter-role-select">
                <SelectValue placeholder="All Roles" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                {JOB_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterDivision} onValueChange={setFilterDivision}>
              <SelectTrigger className="w-full sm:w-48 h-10 rounded-sm" data-testid="filter-division-select">
                <SelectValue placeholder="All Divisions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Divisions</SelectItem>
                {DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full sm:w-40 h-10 rounded-sm" data-testid="filter-status-select">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {EMP_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                <SelectItem value="exited">Exited (legacy)</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card className="border border-neutral-200 shadow-sm rounded-md overflow-hidden">
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow className="bg-neutral-50">
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden md:table-cell">Code</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Name</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden sm:table-cell">Email</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Role</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">Branch</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden lg:table-cell">Division</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 hidden xl:table-cell">Brand</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500">Status</TableHead>
                {canManage && <TableHead className="text-xs font-bold uppercase tracking-wider text-neutral-500 text-right">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <TableRow key={i}><TableCell colSpan={8}><div className="h-10 bg-neutral-100 rounded animate-pulse" /></TableCell></TableRow>
                ))
              ) : employees.length === 0 ? (
                <TableRow><TableCell colSpan={8} className="text-center py-12 text-neutral-500">
                  <Users className="h-8 w-8 mx-auto mb-2 text-neutral-300" /> No employees found
                </TableCell></TableRow>
              ) : employees.map(emp => (
                <TableRow key={emp.id} className="hover:bg-neutral-50 cursor-pointer" data-testid={`employee-row-${emp.id}`}
                  onClick={() => navigate(`/employees/${emp.id}/profile`)}>
                  <TableCell className="text-neutral-600 text-xs font-mono hidden md:table-cell">{emp.employee_code || '-'}</TableCell>
                  <TableCell className="font-medium text-neutral-900 text-sm">{emp.name}</TableCell>
                  <TableCell className="text-neutral-600 text-sm hidden sm:table-cell">{emp.email}</TableCell>
                  <TableCell>
                    <Badge variant="secondary" className={`text-xs ${['Sales Girl', 'Sales Man'].includes(emp.job_role) ? 'bg-red-50 text-red-700' : ''}`}>
                      {emp.job_role}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-neutral-600 text-sm hidden lg:table-cell">{getBranchName(emp.branch_id)}</TableCell>
                  <TableCell className="text-neutral-600 text-sm hidden lg:table-cell">{emp.division || '-'}</TableCell>
                  <TableCell className="hidden xl:table-cell">
                    {emp.brand_classification ? (
                      <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{emp.brand_classification}</Badge>
                    ) : ['Sales Girl', 'Sales Man'].includes(emp.job_role) ? (
                      <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">Not assigned</Badge>
                    ) : (
                      <span className="text-neutral-400 text-xs">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={`text-[10px] capitalize ${STATUS_BADGE[emp.emp_status || 'active'] || 'bg-neutral-50 text-neutral-600'}`}
                      data-testid={`status-badge-${emp.id}`}>
                      {emp.emp_status || 'active'}
                    </Badge>
                  </TableCell>
                  {canManage && (
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleEdit(emp); }} data-testid={`edit-employee-${emp.id}`}>
                          <Edit className="h-3.5 w-3.5 text-neutral-500" />
                        </Button>
                        {emp.emp_status === 'exited' && (
                          <Button variant="ghost" size="icon" className="h-8 w-8"
                            title="Download Experience Certificate"
                            onClick={(e) => { e.stopPropagation(); handleDownloadCertificate(emp); }}
                            data-testid={`exp-cert-${emp.id}`}>
                            <FileText className="h-3.5 w-3.5 text-blue-600" />
                          </Button>
                        )}
                        {user?.system_role !== 'branch_manager' && !emp.is_primary_admin && (
                          <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => { e.stopPropagation(); handleDelete(emp.id); }} data-testid={`delete-employee-${emp.id}`}>
                            <Trash2 className="h-3.5 w-3.5 text-red-500" />
                          </Button>
                        )}
                        {emp.is_primary_admin && user?.system_role !== 'branch_manager' && (
                          <span className="inline-flex items-center px-2 py-0.5 rounded-sm text-[10px] font-semibold bg-red-50 text-red-700 border border-red-100" title="Primary Super Admin — protected from deletion & role change">
                            Primary
                          </span>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="employee-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading font-bold">{editingId ? 'Edit Employee' : 'Add Employee'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">Basic Info</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Name *</Label>
                <Input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))}
                  className="rounded-sm" data-testid="employee-name-input" />
              </div>
              {!editingId && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Login Email *</Label>
                    <Input value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))}
                      className="rounded-sm" data-testid="employee-email-input" placeholder="user@konattu.com" />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Login Password *</Label>
                    <Input type="text" value={form.password}
                      onChange={e => setForm(p => ({ ...p, password: e.target.value }))}
                      className="rounded-sm" data-testid="employee-password-input"
                      placeholder="Temporary password" />
                    <p className="text-[10px] text-neutral-600">Share this with the employee — they can change it after login.</p>
                  </div>
                </>
              )}
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Phone *</Label>
                <Input value={form.phone} onChange={e => setForm(p => ({ ...p, phone: e.target.value }))}
                  className="rounded-sm" data-testid="employee-phone-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Gender</Label>
                <Select value={form.gender} onValueChange={v => setForm(p => ({ ...p, gender: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="male">Male</SelectItem>
                    <SelectItem value="female">Female</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Date of Birth</Label>
                <Input type="date" value={form.dob} onChange={e => setForm(p => ({ ...p, dob: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Blood Group</Label>
                <Select value={form.blood_group} onValueChange={v => setForm(p => ({ ...p, blood_group: v }))}>
                  <SelectTrigger className="rounded-sm"><SelectValue placeholder="Select" /></SelectTrigger>
                  <SelectContent>
                    {['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map(g => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label className="text-sm font-medium">Address</Label>
              <Input value={form.address} onChange={e => setForm(p => ({ ...p, address: e.target.value }))} className="rounded-sm" />
            </div>

            {editingId && user?.system_role === 'super_admin' && (
              <>
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 pt-2" data-testid="credentials-section-title">Login Credentials (Super Admin)</p>
                <div className="rounded-sm border border-amber-200 bg-amber-50/60 px-3 py-3 space-y-3">
                  <div className="grid grid-cols-2 gap-3">
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium">Login Email</Label>
                      <Input
                        type="email"
                        value={creds.email}
                        onChange={e => setCreds(c => ({ ...c, email: e.target.value }))}
                        className="rounded-sm bg-white"
                        data-testid="credentials-email-input"
                        placeholder="user@konattu.com"
                      />
                    </div>
                    <div className="space-y-1.5">
                      <Label className="text-sm font-medium">Reset Password</Label>
                      <Input
                        type="text"
                        value={creds.password}
                        onChange={e => setCreds(c => ({ ...c, password: e.target.value }))}
                        className="rounded-sm bg-white"
                        data-testid="credentials-password-input"
                        placeholder="Leave blank to keep current"
                        autoComplete="new-password"
                      />
                    </div>
                  </div>
                  <p className="text-[11px] text-amber-800 leading-snug">
                    Editing these changes how the employee logs in. Email must be unique. Leave password blank to keep current. Changes are audit-logged.
                  </p>
                </div>
              </>
            )}

            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 pt-2">Work Info</p>
            {lockStructural && (
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm px-2 py-1" data-testid="structural-lock-note">
                Branch, Division, Job Role, Brand Classification & System Role can only be changed by Super Admin or Management.
              </p>
            )}
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Branch *</Label>
                <Select value={form.branch_id} onValueChange={v => setForm(p => ({ ...p, branch_id: v }))} disabled={lockStructural}>
                  <SelectTrigger className="rounded-sm" data-testid="employee-branch-select"><SelectValue placeholder="Select branch" /></SelectTrigger>
                  <SelectContent>{branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Division</Label>
                <Select value={form.division} onValueChange={v => setForm(p => ({ ...p, division: v }))} disabled={lockStructural}>
                  <SelectTrigger className="rounded-sm" data-testid="employee-division-select"><SelectValue placeholder="Select division" /></SelectTrigger>
                  <SelectContent>{DIVISIONS.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Job Role *</Label>
                <Select value={form.job_role} onValueChange={v => setForm(p => ({ ...p, job_role: v }))} disabled={lockStructural}>
                  <SelectTrigger className="rounded-sm" data-testid="employee-job-role-select"><SelectValue placeholder="Select role" /></SelectTrigger>
                  <SelectContent>{JOB_ROLES.map(r => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5 col-span-2 sm:col-span-1">
                <Label className="text-sm font-medium flex items-center gap-1.5">
                  Brand Classification
                  {form.brand_classification ? (
                    <Badge variant="outline" className="text-[10px] bg-amber-50 text-amber-700 border-amber-200">{form.brand_classification}</Badge>
                  ) : (
                    <Badge variant="outline" className="text-[10px] bg-red-50 text-red-600 border-red-200">Not assigned</Badge>
                  )}
                </Label>
                <Select value={form.brand_classification || '__none__'} onValueChange={v => setForm(p => ({ ...p, brand_classification: v === '__none__' ? '' : v }))} disabled={lockStructural}>
                  <SelectTrigger className="rounded-sm" data-testid="employee-brand-classification-select"><SelectValue placeholder="Select brand" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">— Not assigned —</SelectItem>
                    {brandList.length > 0
                      ? brandList.map(b => <SelectItem key={b.id} value={b.name}>{b.name}</SelectItem>)
                      : BRAND_CLASSIFICATIONS.map(b => <SelectItem key={b} value={b}>{b}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-[10px] text-neutral-500">Determines the staff's Brand Achievements target & mapping.</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">System Role</Label>
                <Select value={form.system_role} onValueChange={v => setForm(p => ({ ...p, system_role: v }))} disabled={lockStructural || editingIsPrimary}>
                  <SelectTrigger className="rounded-sm" data-testid="employee-system-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{SYSTEM_ROLES.map(r => <SelectItem key={r} value={r}>{r.replace('_', ' ')}</SelectItem>)}</SelectContent>
                </Select>
                {editingIsPrimary && (
                  <p className="text-[10px] text-red-600">Primary Super Admin — role is locked and cannot be changed.</p>
                )}
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Joining Date</Label>
                <Input type="date" value={form.joining_date} onChange={e => setForm(p => ({ ...p, joining_date: e.target.value }))} className="rounded-sm" />
              </div>
              {editingId && (
                <>
                  <div className="space-y-1.5">
                    <Label className="text-sm font-medium">Employee Status</Label>
                    <Select
                      value={form.emp_status || 'active'}
                      onValueChange={v => setForm(p => ({ ...p, emp_status: v }))}
                      disabled={!canChangeStatus || editingIsPrimary}
                    >
                      <SelectTrigger className="rounded-sm" data-testid="employee-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {EMP_STATUSES.map(s => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}
                        {form.emp_status === 'exited' && <SelectItem value="exited">Exited (legacy)</SelectItem>}
                      </SelectContent>
                    </Select>
                    {!canChangeStatus && (
                      <p className="text-[10px] text-neutral-500">Only Super Admin, Management, or H.R. can change lifecycle status.</p>
                    )}
                    {editingIsPrimary && (
                      <p className="text-[10px] text-red-600">Primary Super Admin's status cannot be changed.</p>
                    )}
                  </div>
                  {(form.emp_status && form.emp_status !== 'active') && (
                    <>
                      <div className="space-y-1.5">
                        <Label className="text-sm font-medium">Exit / Effective Date</Label>
                        <Input
                          type="date"
                          value={form.exit_date || ''}
                          onChange={e => setForm(p => ({ ...p, exit_date: e.target.value }))}
                          className="rounded-sm"
                          disabled={!canChangeStatus}
                        />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-sm font-medium">Reason / Notes</Label>
                        <Input
                          value={form.exit_reason || ''}
                          onChange={e => setForm(p => ({ ...p, exit_reason: e.target.value }))}
                          className="rounded-sm"
                          disabled={!canChangeStatus}
                          placeholder="e.g. resignation letter dated 12 Feb"
                        />
                      </div>
                    </>
                  )}
                </>
              )}
            </div>

            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 pt-2">ID & Bank Details</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Aadhar Number</Label>
                <Input value={form.aadhar_number} onChange={e => setForm(p => ({ ...p, aadhar_number: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">PAN Number</Label>
                <Input value={form.pan_number} onChange={e => setForm(p => ({ ...p, pan_number: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">UAN Number</Label>
                <Input value={form.uan_number} onChange={e => setForm(p => ({ ...p, uan_number: e.target.value }))} className="rounded-sm" data-testid="employee-uan-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">ESI Number</Label>
                <Input value={form.esi_number} onChange={e => setForm(p => ({ ...p, esi_number: e.target.value }))} className="rounded-sm" data-testid="employee-esi-input" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Bank Account</Label>
                <Input value={form.bank_account} onChange={e => setForm(p => ({ ...p, bank_account: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Bank Name</Label>
                <Input value={form.bank_name} onChange={e => setForm(p => ({ ...p, bank_name: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">IFSC Code</Label>
                <Input value={form.ifsc_code} onChange={e => setForm(p => ({ ...p, ifsc_code: e.target.value }))} className="rounded-sm" />
              </div>
            </div>

            <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 pt-2">Emergency & Education</p>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Emergency Contact</Label>
                <Input value={form.emergency_contact} onChange={e => setForm(p => ({ ...p, emergency_contact: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Emergency Name</Label>
                <Input value={form.emergency_name} onChange={e => setForm(p => ({ ...p, emergency_name: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Education</Label>
                <Input value={form.education} onChange={e => setForm(p => ({ ...p, education: e.target.value }))} className="rounded-sm" placeholder="e.g., B.Com, MBA" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Experience (Years)</Label>
                <Input type="number" value={form.experience_years} onChange={e => setForm(p => ({ ...p, experience_years: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5 col-span-2">
                <Label className="text-sm font-medium">Previous Company</Label>
                <Input value={form.previous_company} onChange={e => setForm(p => ({ ...p, previous_company: e.target.value }))} className="rounded-sm" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Entry Time <span className="text-xs text-neutral-600 font-normal">(24h IST)</span></Label>
                <Input type="time" value={form.entry_time}
                  onChange={e => setForm(p => ({ ...p, entry_time: e.target.value }))}
                  className="rounded-sm" data-testid="employee-entry-time-input" />
                <p className="text-[10px] text-neutral-600">5-min grace · after that punch-in = auto Half Day</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Exit Time <span className="text-xs text-neutral-600 font-normal">(24h IST)</span></Label>
                <Input type="time" value={form.exit_time}
                  onChange={e => setForm(p => ({ ...p, exit_time: e.target.value }))}
                  className="rounded-sm" data-testid="employee-exit-time-input" />
                <p className="text-[10px] text-neutral-600">Worked &lt; half = Leave · &lt; full = Half Day</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Sunday Entry <span className="text-xs text-neutral-600 font-normal">(optional)</span></Label>
                <Input type="time" value={form.sunday_entry_time || ''}
                  onChange={e => setForm(p => ({ ...p, sunday_entry_time: e.target.value }))}
                  className="rounded-sm" data-testid="employee-sunday-entry-time-input" />
                <p className="text-[10px] text-neutral-600">Used only on Sundays · leave blank to follow weekday timing</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-sm font-medium">Sunday Exit <span className="text-xs text-neutral-600 font-normal">(optional)</span></Label>
                <Input type="time" value={form.sunday_exit_time || ''}
                  onChange={e => setForm(p => ({ ...p, sunday_exit_time: e.target.value }))}
                  className="rounded-sm" data-testid="employee-sunday-exit-time-input" />
                <p className="text-[10px] text-neutral-600">Used only on Sundays · leave blank to follow weekday timing</p>
              </div>
              <div className="col-span-2 mt-2 border border-neutral-200 rounded-sm p-3 bg-neutral-50/60">
                <label className="flex items-start gap-3 cursor-pointer" data-testid="require-achievement-toggle-label">
                  <input
                    type="checkbox"
                    checked={!!form.require_achievement_before_attendance}
                    onChange={e => setForm(p => ({ ...p, require_achievement_before_attendance: e.target.checked }))}
                    className="mt-1 h-4 w-4 rounded border-neutral-300 accent-red-600"
                    data-testid="require-achievement-toggle"
                  />
                  <div className="flex-1">
                    <p className="text-sm font-medium text-neutral-900">Require Achievement Before Attendance</p>
                    <p className="text-[11px] text-neutral-600 mt-0.5">
                      When <strong>ON</strong>, this employee's attendance is blocked if they miss achievement entries for 2 consecutive days (starting Day 3 from account creation). Submitting an entry unblocks immediately.
                    </p>
                  </div>
                </label>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-sm">Cancel</Button>
            <Button onClick={handleSave} className="bg-red-600 hover:bg-red-700 text-white rounded-sm" data-testid="employee-save-button">
              {editingId ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
